import { getConfig } from '@superbet-group/web.lib.config';
import { differenceInDays } from '@superbet-group/web.lib.utilities';
import { Dispatch } from 'vuex';

import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { PushMessageType } from '@core/models/user/enums';
import {
    CashBonusData,
    CompletedBonusData,
    ExpiredBonusData,
    MessageData,
    NewBonusData,
    TermsUpdateData,
} from '@core/models/user/pushNotifications/MessageData';
import {
    ExpiredBonusStatus,
    MessageBonusType,
    MessageButton,
    MessageModalData,
} from '@src/app/modals/modals/messages/types';
import Action from '@core/models/user/pushNotifications/Action';

const daysUntilWOReappear = 3;
const emptyMessage: MessageModalData = {
    title: '',
    description: '',
    imgSrc: '',
};

export function getMessageDetails(messageType?: PushMessageType, messageData?: MessageData): MessageModalData {
    if (!messageData) {
        return emptyMessage;
    }

    switch (messageType) {
        case PushMessageType.ExpiredBonus:
            const expiredData = messageData as ExpiredBonusData;
            return {
                title: 'bonus.popup.title_expired',
                description: expiredBonusDescription(expiredData),
                imgSrc: 'bonus-expired',
            };
        case PushMessageType.CompletedBonus:
            if ((messageData as CashBonusData).bonusType === MessageBonusType.CashBonus) {
                return {
                    title: 'bonus.popup.cash_bonus_title',
                    description: 'bonus.popup.bonus_completed.cash_bonus.description',
                    imgSrc: 'bonus-completed',
                };
            } else {
                const completedData = messageData as CompletedBonusData;
                return {
                    title: completedData.winnings > 0
                        ? 'bonus.popup.title_completed_win'
                        : 'bonus.popup.title_completed_lost',
                    description: completedBonusDescription(completedData),
                    imgSrc: 'bonus-completed',
                };
            }
        case PushMessageType.NewBonus:
            const newData = messageData as NewBonusData;
            return {
                title: 'bonus.popup.title_bonus_received',
                description: newBonusDescription(newData),
                imgSrc: 'bonus',
            };
        case PushMessageType.TermsUpdate:
            const termsData = messageData as TermsUpdateData;
            return {
                title: termsData.title,
                description: termsData.text,
                imgSrc: '',
            };
        default:
            return emptyMessage;
    }
}

function expiredBonusDescription(messageData: ExpiredBonusData): string {
    if (messageData.state === ExpiredBonusStatus.New) {
        return 'bonus.popup.description_not_used';
    } else if (messageData.winnings > 0) {
        return 'bonus.popup.description_partially_used';
    } else {
        return 'bonus.popup.description_expired_no_wins';
    }
}

function completedBonusDescription(messageData: CompletedBonusData): string {
    if (messageData.winnings > 0) {
        return messageData.bonusType === MessageBonusType.FreeSpins ? 'bonus.popup.bonus_completed.free_spins.description' : 'bonus.popup.bonus_completed.win.description';
    } else {
        return 'bonus.popup.description_completed_no_win';
    }
}

function newBonusDescription(messageData: NewBonusData): string {
    const sportBonusFundsKey = messageData.redeemable ? 'bonus.popup.description_bonus_received.sport' : 'bonus.popup.description_bonus_received.free_bet';
    const bonusTypeDescription: Record<MessageBonusType, string> = {
        [MessageBonusType.FreeSpins]: 'bonus.popup.description_bonus_received.free-spins',
        [MessageBonusType.BonusFunds]: messageData.domain === 'gaming' ? 'bonus.popup.description_bonus_received.casino' : sportBonusFundsKey,
        [MessageBonusType.CashBonus]: '',
        [MessageBonusType.Wagering]: messageData.domain === 'gaming' ? 'bonus.popup.description_bonus_received.casino' : 'bonus.popup.description_bonus_received.sport',
    };

    return bonusTypeDescription[messageData.bonusType];
}

export function isModalDismissable(messageType?: PushMessageType): boolean {
    if (!messageType) {
        return false;
    }

    const dismissableModals: PushMessageType[] = [
        PushMessageType.BonusIsAwarded,
        PushMessageType.BonusAbuser,
        PushMessageType.NewBonus,
        PushMessageType.ExpiredBonus,
        PushMessageType.CompletedBonus,
        PushMessageType.KYCStatusChangedToPassed,
    ];

    return dismissableModals.includes(messageType);
}

export function isModalClosable(messageType?: PushMessageType, shouldShowNewEligibleForCampaignModal?: boolean): boolean {
    if (!messageType) {
        return false;
    }

    const closableModals: PushMessageType[] = [
        PushMessageType.BonusIsAwarded,
        PushMessageType.BonusAbuser,
        PushMessageType.KYCStatusChangedToPassed,
        PushMessageType.WithdrawalStatusChangesToPaid,
    ];

    if (!shouldShowNewEligibleForCampaignModal) {
        closableModals.push(PushMessageType.EligibleForCampaign);
    }

    return closableModals.includes(messageType);
}

export async function shouldShowWelcomeBonusMessage(dispatch: Dispatch, rootGetters: any) {
    const user = rootGetters['data/authentication/user'];
    if (user?.welcomeBonus
        && !user.bonusAbuser
        && getConfig().countrySpecific.user.bonus.isWelcomeBonusShown
    ) {
        await dispatch('ui/user/bonuses/fetchWelcomeBonuses', null, { root: true });
        return rootGetters['ui/user/bonuses/areWelcomeBonusesFetched'];
    } else {
        return false;
    }
}

/**
 * Once WelcomeOffer modal is dismissed it should appear again
 * after number of days saved in 'daysUntilWOReappear' constant
 */
export function shouldWelcomeOfferModalReappear(userId: number, welcomeOfferCampaignId: string | null) {
    if (isWelcomeOfferDismissed(userId, welcomeOfferCampaignId)) {
        const welcomeOfferStorageData = clientStorageWithExpiry.get<Record<string, string>>(`welcomeOffer:${userId}`)!;
        return differenceInDays(new Date(), new Date(welcomeOfferStorageData.dismissedDate)) >= daysUntilWOReappear;
    } else {
        return true;
    }
}

export function isWelcomeOfferDismissed(userId: number, welcomeOfferCampaignId: string | null) {
    const welcomeOfferStorageData = clientStorageWithExpiry.get<Record<string, string>>(`welcomeOffer:${userId}`);
    return welcomeOfferStorageData?.welcomeOfferCampaignId === welcomeOfferCampaignId;
}

export function shouldShowNewEligibleForCampaignModal(messageType: PushMessageType | undefined, actions: Action[]) {
    return messageType === PushMessageType.EligibleForCampaign && actions?.some((a) => a.name === MessageButton.Ok);
}
